import { padNumber } from '@utils/format-utils';
import moment from 'moment';
import type { Moment } from 'moment';

/** UK-style calendar date + time + numeric offset with colon (e.g. `+01:00`). */
const DISPLAY_DATETIME_WITH_OFFSET = 'DD/MM/YYYY HH:mm:ss Z';

// https://devhints.io/moment
const FORMAT_DATE_INPUT_FORMATS: (string | typeof moment.ISO_8601)[] = [
  moment.ISO_8601,
  'YYYY-MM-DD',
  'DD/MM/YYYY',
  'DD-MM-YYYY',
  'MM/DD/YYYY',
];

export function formatDate(
  date: string | undefined | null | Date,
  format = 'DD/MM/YYYY',
  valueWhenNullUndefinedOrEmpty = '-'
) {
  if (!(date instanceof Date) && !(date && date.length)) return valueWhenNullUndefinedOrEmpty;

  if (date instanceof Date) {
    const m = moment.utc(date);
    return m.isValid() ? m.format(format) : 'Invalid date';
  }

  const trimmed = date.trim();
  for (const inputFormat of FORMAT_DATE_INPUT_FORMATS) {
    const m = moment.utc(trimmed, inputFormat, true);
    if (m.isValid()) {
      return m.format(format);
    }
  }

  return 'Invalid date';
}

export function formatTime(time: string | undefined | null, _format: 'LT' | 'LTS' = 'LT', storedFormat = 'hh:mm a') {
  if (!(time && time.length)) {
    return '';
  }

  const parsedDate = new Date(time);
  if (Number.isNaN(parsedDate.getTime())) {
    console.error('error parsing time string to date. string: ' + time);
    return '';
  }
  return moment(parsedDate).format(storedFormat);
}

/** True when the string ends with a zone designator (`Z` or `±hh:mm` / `±hhmm`). */
export function isoStringHasExplicitOffset(value: string): boolean {
  const s = value.trim();
  if (!/T/i.test(s)) {
    return false;
  }
  return /(?:\.\d+)?Z\s*$/i.test(s) || /[+-]\d{2}(?::?\d{2})?\s*$/i.test(s);
}

/**
 * Formats a date-time as UK-style date + time + offset (`DD/MM/YYYY HH:mm:ss Z`, e.g. `+01:00`).
 *
 * @param convertTimezone When `true`, formats in local time (runtime/browser zone) and includes the local offset.
 *   When `false` (default), keeps wall clock and offset from ISO strings (`moment.parseZone`).
 * @param defaultUtcOffset When `convertTimezone` is `false`, optional display offset (minutes east
 *   of UTC or a string such as `'+01:00'`) applied to `Date` values and to strings with no explicit
 *   zone (`utcOffset(..., true)` so digits stay the same).
 */
export function formatDateTimeWithTimezone(
  date: string | undefined | null | Date,
  convertTimezone = false,
  defaultUtcOffset?: number | string
): string {
  const valueWhenNullUndefinedOrEmpty = '-';
  if (date == null || date === '') return valueWhenNullUndefinedOrEmpty;
  if (typeof date === 'string' && !date.trim()) return valueWhenNullUndefinedOrEmpty;

  if (convertTimezone) {
    // Preserve existing "display in local zone" behaviour, but avoid native Date parsing for
    // UK-formatted strings (implementation-dependent; can flip DD/MM ↔ MM/DD).
    if (typeof date === 'string') {
      const raw = date.trim();

      // e.g. `01/04/2026 08:15:33 +01:00`
      const ukWithOffset = moment(raw, DISPLAY_DATETIME_WITH_OFFSET, true);
      if (ukWithOffset.isValid()) {
        return moment(ukWithOffset.toDate()).format(DISPLAY_DATETIME_WITH_OFFSET);
      }

      const m = moment(new Date(raw));
      if (!m.isValid()) return valueWhenNullUndefinedOrEmpty;
      return m.format(DISPLAY_DATETIME_WITH_OFFSET);
    }

    const m = moment(date);
    if (!m.isValid()) return valueWhenNullUndefinedOrEmpty;
    return m.format(DISPLAY_DATETIME_WITH_OFFSET);
  }

  let m = date instanceof Date ? moment(date) : moment.parseZone(date);
  if (!m.isValid()) return valueWhenNullUndefinedOrEmpty;

  if (defaultUtcOffset != null) {
    if (date instanceof Date) {
      m = moment(date).utcOffset(defaultUtcOffset);
    } else if (typeof date === 'string' && !isoStringHasExplicitOffset(date)) {
      m = moment.parseZone(date).utcOffset(defaultUtcOffset, true);
    }
  }

  return m.format(DISPLAY_DATETIME_WITH_OFFSET);
}

/**
 * Formats a date-time in local time with Moment (see https://devhints.io/moment).
 * Unlike {@link formatDate}, input is not normalised through UTC first.
 * Same parsing approach as {@link formatTime} (`Date` then `moment`).
 *
 * @returns `undefined` when the value is missing, blank, or cannot be parsed.
 */
export function formatDateLocal(
  date: string | Date | null | undefined,
  format: string
): string | undefined {
  if (date == null || date === '') {
    return undefined;
  }
  if (typeof date === 'string' && !date.trim()) {
    return undefined;
  }
  const m = moment(date instanceof Date ? date : new Date(date));
  if (!m.isValid()) {
    return undefined;
  }
  return m.format(format);
}

/**
 * UTC calendar date as `YYYY-MM-DD` for `app-date-time-picker` when no time is selected
 * (aligns with {@link MomentUtcDateAdapter}).
 */
export function formatUtcCalendarDateOnlyFromPickerDate(date: Date | Moment): string | null {
  const m = moment.isMoment(date) ? date.clone().utc() : moment.utc(date);
  return m.isValid() ? m.format('YYYY-MM-DD') : null;
}

/**
 * Combines a picker date with a local wall-clock `HH:mm` and returns an ISO string (same behaviour
 * as the picker’s date+time branch).
 */
export function combinePickerDateAndLocalWallTimeToUtc(date: Date | Moment, timeHm: string): string {
  const d = new Date(moment.isMoment(date) ? date.toDate() : date);
  const [h, m] = String(timeHm).split(':').map(Number);
  d.setHours(h || 0, m || 0, 0, 0);
  return formatWithOffset(d, false);
}

/**
 * Nested `date` / `time` values from {@link parseDateTimePickerWriteValue} for `app-date-time-picker`.
 */
export interface DateTimePickerWriteParts {
  /** Calendar instant for the date portion (Material / UTC adapter). */
  date: Date;
  /** `HH:mm` in local wall time, or `null` when the parent value is date-only / UTC midnight sentinel. */
  time: string | null;
}

/** Normalises parent control values (string / `Date` / Moment) to a string for {@link parseDateTimePickerWriteValue}. */
function coerceDateTimePickerParentValueToString(value: unknown): string | null {
  if (value == null || value === '') {
    return null;
  }
  if (typeof value === 'string') {
    return value;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.toISOString();
  }
  if (moment.isMoment(value)) {
    const m = value as Moment;
    return m.isValid() ? m.toISOString() : null;
  }
  return null;
}

export function hasTimePart(date: string | Date | null): boolean {
  return date?.toString().includes('T') ?? false;
}

/**
 * UTC midnight (`…T00:00:00.000Z`) for the UTC calendar day of instant `d`.
 */
export function formatUtcCalendarDayStartIso(d: Date): string | null {
  if (!d || Number.isNaN(d.getTime())) {
    return null;
  }
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  const day = d.getUTCDate();
  return new Date(Date.UTC(y, m, day, 0, 0, 0, 0)).toISOString();
}

/**
 * UTC end of day (`…T23:59:59.999Z`) for the UTC calendar day of instant `d`.
 */
export function formatUtcCalendarDayEndIso(d: Date): string | null {
  if (!d || Number.isNaN(d.getTime())) {
    return null;
  }
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  const day = d.getUTCDate();
  return new Date(Date.UTC(y, m, day, 23, 59, 59, 999)).toISOString();
}

/**
 * Maps a parent `FormControl` value into nested date/time picker state for `app-date-time-picker`:
 * `YYYY-MM-DD` and UTC-midnight ISO values with an explicit zone (`Z` / offset) use `time: null`
 * (avoids showing 01:00 in BST when UTC is 00:00); offset-free `T00:00:00` is user wall-clock midnight.
 * otherwise uses local hours/minutes for `time`.
 *
 * Accepts ISO / calendar strings, `Date`, or Moment; other types yield `null`.
 */
export function parseDateTimePickerWriteValue(value: unknown): DateTimePickerWriteParts | null {
  const inputStr = coerceDateTimePickerParentValueToString(value);
  if (inputStr == null) {
    return null;
  }
  const trimmed = inputStr.trim();
  if (!trimmed) {
    return null;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const parsed = moment.utc(trimmed, 'YYYY-MM-DD', true);
    if (!parsed.isValid()) {
      return null;
    }
    return { date: parsed.toDate(), time: null };
  }

  const utc = moment.utc(trimmed, moment.ISO_8601, true);
  if (
    isoStringHasExplicitOffset(trimmed) &&
    utc.isValid() &&
    utc.hour() === 0 &&
    utc.minute() === 0 &&
    utc.second() === 0 &&
    utc.millisecond() === 0
  ) {
    return { date: utc.toDate(), time: null };
  }

  const d = new Date(trimmed);
  if (Number.isNaN(d.getTime())) {
    return null;
  }
  const hours = d.getHours().toString().padStart(2, '0');
  const minutes = d.getMinutes().toString().padStart(2, '0');
  return { date: d, time: `${hours}:${minutes}` };
}

export function getShortTextDateTime(date: Date | null | undefined, valueWhenNullUndefinedOrEmpty = '-', locale = 'default'): string {
  if (!date) return valueWhenNullUndefinedOrEmpty;

  const thisDate = new Date(date);
  if (isNaN(thisDate.getTime())) return valueWhenNullUndefinedOrEmpty;

  const datePart = thisDate.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: '2-digit'
  }).replace(',', ''); // Remove any commas from the date part

  const timePart = thisDate.toLocaleTimeString(locale, {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit'
  });

  return `${datePart}, ${timePart}`;
}

export function extractDateFromString(date: string) {
  if (!(date && date.length)) return null;
  return moment(date).toDate();
}

/**
 * Parses a form/API value (ISO string or Date) to a Date for comparison or validation.
 * @param value Value from a date-time picker or form control
 * @returns Date or null if missing/invalid
 */
export function parseDateValue(
  value: string | Date | null | undefined
): Date | null {
  if (value == null || value === '') return null;
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

/**
 * First instant of the first day of the next calendar month in UTC.
 * Used when vault service plan effective-from must default to the earliest allowed future month (not the stored API value).
 */
export function getNextCalendarMonthUtcStart(now: Date = new Date()): Date {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
}

/**
 * UTC calendar year and month (0–11) for ordering year-month form values.
 * Handles `Date` and Moment (Material datepicker often stores Moment on the control).
 */
export function getUtcYearMonthForRange(value: unknown): { year: number; month: number } | null {
  if (value == null || value === '') return null;
  if (moment.isMoment(value)) {
    if (!value.isValid()) return null;
    const u = moment.utc(value).startOf('month');
    return { year: u.year(), month: u.month() };
  }
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return { year: value.getUTCFullYear(), month: value.getUTCMonth() };
  }
  if (typeof value === 'string') {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return { year: d.getUTCFullYear(), month: d.getUTCMonth() };
  }
  return null;
}

/**
 * Parses a date string (DD-MM-YYYY, YYYY-MM-DD, or other formats) to a Date object
 * Normalizes to first day of month for date picker components
 * Explicitly handles DD-MM-YYYY format to avoid American date format issues
 * @param dateValue Date string or Date object
 * @returns Date object normalized to first day of month, or null if invalid
 */
export function parseDateForPicker(dateValue: string | Date | null | undefined): Date | null {
  if (!dateValue) return null;

  // If it's already a Date object, normalize to first of month
  if (dateValue instanceof Date) {
    return moment.utc(dateValue).startOf('month').toDate();
  }
  
  if (typeof dateValue === 'string') {
    // Handle DD-MM-YYYY format explicitly (from API) to avoid American format issues
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateValue)) {
      const parsed = moment.utc(dateValue, 'DD-MM-YYYY', true);
      if (parsed.isValid()) {
        return parsed.startOf('month').toDate();
      }
    }

    // Try YYYY-MM-DD format (ISO)
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      const parsed = moment.utc(dateValue, 'YYYY-MM-DD', true);
      if (parsed.isValid()) {
        return parsed.startOf('month').toDate();
      }
    }

    // Fallback to moment's flexible parsing
    const parsed = moment.utc(dateValue);
    if (parsed.isValid()) {
      return parsed.startOf('month').toDate();
    }
  }

  return null;
}

export function formatWithOffset(date: Date, useOffset = true): string {
  const ms = padNumber(date.getMilliseconds(), 3);
  const y = date.getFullYear();
  const mo = padNumber(date.getMonth() + 1, 2);
  const da = padNumber(date.getDate(), 2);
  const h = padNumber(date.getHours(), 2);
  const mi = padNumber(date.getMinutes(), 2);
  const s = padNumber(date.getSeconds(), 2);
  const base = `${y}-${mo}-${da}T${h}:${mi}:${s}.${ms}`;

  if (!useOffset) {
    return base;
  }

  const offset = -date.getTimezoneOffset();
  const sign = offset >= 0 ? '+' : '-';
  const absOffset = Math.abs(offset);
  const offsetHours = padNumber(Math.floor(absOffset / 60), 2);
  const offsetMinutes = padNumber(absOffset % 60, 2);

  return `${base}${sign}${offsetHours}:${offsetMinutes}`;
}

const END_OF_DAY_HOURS = 23;
const END_OF_DAY_MINUTES = 59;
const END_OF_DAY_SECONDS = 59;
const END_OF_DAY_MS = 999;

function isMidnight(d: Date): boolean {
  return (
    d.getHours() === 0 &&
    d.getMinutes() === 0 &&
    d.getSeconds() === 0 &&
    d.getMilliseconds() === 0
  );
}

function setToEndOfDay(d: Date): void {
  d.setHours(END_OF_DAY_HOURS, END_OF_DAY_MINUTES, END_OF_DAY_SECONDS, END_OF_DAY_MS);
}

/**
 * For "to" date filters: when the value is date-only (no time component),
 * returns the same calendar date at end of day (23:59:59.999). Otherwise returns the date unchanged.
 * Use when sending the "to" date in a request so the range includes the whole day.
 * @param value ISO string or Date from a date-time picker
 * @returns Date at end of day if input was midnight, otherwise the parsed date; null if no/invalid value
 */
export function toDateEndOfDayIfMidnight(
  value: string | Date | null | undefined
): Date | null {
  if (value == null || value === '') return null;
  const d = typeof value === 'string' ? new Date(value) : new Date(value.getTime());
  if (Number.isNaN(d.getTime())) return null;
  if (!isMidnight(d)) return d;
  const endOfDay = new Date(d);
  setToEndOfDay(endOfDay);
  return endOfDay;
}

/**
 * Same as {@link toDateEndOfDayIfMidnight} but returns an ISO string for request payloads.
 * @returns ISO string or null if no/invalid value
 */
export function toDateEndOfDayIfMidnightAsIso(
  value: string | Date | null | undefined
): string | null {
  const d = toDateEndOfDayIfMidnight(value);
  return d ? d.toISOString() : null;
}

/**
 * Formats a date value to ISO format for API consumption
 * Handles moment objects, strings, Date objects, and various date formats
 * @param dateValue The date value to format
 * @param format The output format (default: 'YYYY-MM-DDTHH:mm:ss.SSSZ')
 * @returns Formatted date string in ISO format or null if invalid
 */
export function formatDateForApi(dateValue: any, format = 'YYYY-MM-DDTHH:mm:ss.SSSZ'): string | null {
  if (!dateValue) return null;

  if (moment.isMoment(dateValue)) {
    return dateValue.isValid() ? dateValue.toISOString() : null;
  }

  if (typeof dateValue === 'string' || dateValue instanceof Date) {
    return formatDate(dateValue, format);
  }

  return null;
}

/**
 * Formats a date value to DD-MM-YYYY format for API consumption
 * Handles moment objects, strings, Date objects, and various date formats
 * @param dateValue The date value to format
 * @returns Formatted date string in DD-MM-YYYY format or empty string if invalid
 */
export function formatDateForApiDDMMYYYY(dateValue: any): string {
  if (!dateValue) return '';

  if (moment.isMoment(dateValue)) {
    return dateValue.isValid() ? dateValue.format('DD-MM-YYYY') : '';
  }

  if (typeof dateValue === 'string' || dateValue instanceof Date) {
    return formatDate(dateValue, 'DD-MM-YYYY');
  }

  return '';
}

function coercePickerValueToDate(dateValue: unknown): Date | null {
  if (dateValue == null || dateValue === '') return null;
  if (moment.isMoment(dateValue)) {
    return dateValue.isValid() ? dateValue.toDate() : null;
  }
  if (dateValue instanceof Date) {
    return Number.isNaN(dateValue.getTime()) ? null : dateValue;
  }
  if (typeof dateValue === 'string') {
    const d = new Date(dateValue);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/**
 * Converts a date to YYYY-MM-DD format (date only, no time)
 * Useful for date inputs and date-only fields
 * @param dateValue The date value to convert
 * @returns Formatted date string in YYYY-MM-DD format or null if invalid
 */
export function formatDateForApiDateOnly(dateValue: any): string | null {
  const date = coercePickerValueToDate(dateValue);
  if (!date) return null;
  return date.toISOString().split('T')[0];
}

/**
 * Converts a date to ISO format without milliseconds
 * Useful for API calls that don't need millisecond precision
 * @param dateValue The date value to convert
 * @returns Formatted date string in YYYY-MM-DDTHH:mm:ss format or empty string if invalid
 */
export function formatDateForApiWithoutMilliseconds(dateValue: any): string {
  const date = coercePickerValueToDate(dateValue);
  if (!date) return '';
  return date.toISOString().slice(0, 19);
}
